import { HbrOption, HbrSelect } from '@harbor/elements-react-standalone';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useFetchOpportunity } from '../../../../../hooks/opportunity-hooks';
import {
  useCreatePovTeamMutation,
  useUpdatePovTeamMutation,
  useFetchPovTeam,
  useFetchPovTeamFromSalesforce,
} from '../../../../../hooks/opportunity-pov-hooks';
import { OpportunityPoV } from '../../../../../types/opportunity-pov-items';

interface PoVTeamSelectOption {
  label: string;
  value: string;
}

interface PovTeamFormOptions {
  bdm: PoVTeamSelectOption[];
  tdm: PoVTeamSelectOption[];
  champion: PoVTeamSelectOption[];
}

interface CustomerTeamSectionProps {
  pov: OpportunityPoV;
  selectedProductId: number | null;
  onValidationChange: (isValid: boolean) => void;
  onLoadingChange: (isLoading: boolean) => void;
  showAddCustomerForm: () => void;
}

const style = {
  control: 'w-80 max-w-full min-h-20',
};

const CustomerTeamSection: React.FC<CustomerTeamSectionProps> = ({
  pov,
  selectedProductId,
  onValidationChange,
  onLoadingChange,
  showAddCustomerForm,
}) => {
  const { id: opportunityId } = useParams();
  const { data: opportunity, isLoading: loadingOpportunity } = useFetchOpportunity(opportunityId, { enabled: !!opportunityId });

  // Fetch team data
  const { data: povTeam, isLoading: loadingTeam } = useFetchPovTeam(pov?.id, { enabled: !!pov?.id });
  const { data: sfPovTeam, isLoading: loadingSfTeam } = useFetchPovTeamFromSalesforce(opportunity?.AccountId, { enabled: !!opportunity });

  const isLoading = loadingOpportunity || loadingTeam || loadingSfTeam;
  const [povTeamOptions, setPovTeamOptions] = useState<PovTeamFormOptions>({
    bdm: [],
    tdm: [],
    champion: [],
  });
  const [unresolvedFields, setUnresolvedFields] = useState({
    bdm: false,
    tdm: false,
    champ: false,
  });

  const { mutateAsync: updatePovTeam } = useUpdatePovTeamMutation();
  const { mutateAsync: createPovTeam } = useCreatePovTeamMutation();

  const isFormDirtyRef = useRef(false);
  const [isFormUpdated, setIsFormUpdated] = useState(false);
  const hasInitialized = useRef(false);
  const lastPovId = useRef<number | null>(null);
  const lastProductId = useRef<number | null>(null);

  // Report loading state to parent
  useEffect(() => {
    onLoadingChange(isLoading);
  }, [isLoading, onLoadingChange]);

  const {
    control,
    reset,
    getValues,
    formState: { dirtyFields },
  } = useForm({
    defaultValues: {
      bdm: '',
      tdm: '',
      champion: '',
    },
  });

  // Reset initialization flag when pov or product changes
  useEffect(() => {
    if (pov && pov.id !== lastPovId.current) {
      hasInitialized.current = false;
      lastPovId.current = pov.id;
    }
    if (selectedProductId && selectedProductId !== lastProductId.current) {
      hasInitialized.current = false;
      lastProductId.current = selectedProductId;
    }
  }, [pov, selectedProductId]);

  // Reset form ONLY on initial load, not on subsequent povTeam changes
  // This prevents form field flashing during updates
  useEffect(() => {
    if (!isLoading && povTeam !== undefined && !hasInitialized.current) {
      reset({
        bdm: povTeam?.cust_bdm ?? '',
        tdm: povTeam?.cust_tdm ?? '',
        champion: povTeam?.cust_champ ?? '',
      });
      hasInitialized.current = true;
    }
  }, [povTeam, isLoading, reset]);

  // Build team options from Salesforce data and handle unresolved fields
  useEffect(() => {
    if (isLoading) return;

    const sfTeamOptions = (sfPovTeam ?? [])
      .map((member) => ({
        label: `${member?.FirstName ?? ''} ${member?.LastName}`,
        value: member.Id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const resolvedSfTeamIds = new Set(sfTeamOptions.map((opt) => opt.value));

    const hasBdmUnresolved =
      povTeam?.cust_bdm && povTeam?.cust_bdm !== '' ? !resolvedSfTeamIds.has(povTeam.cust_bdm) : false;
    const hasTdmUnresolved =
      povTeam?.cust_tdm && povTeam?.cust_tdm !== '' ? !resolvedSfTeamIds.has(povTeam.cust_tdm) : false;
    const hasChampUnresolved =
      povTeam?.cust_champ && povTeam?.cust_champ !== '' ? !resolvedSfTeamIds.has(povTeam.cust_champ) : false;

    setUnresolvedFields({
      bdm: hasBdmUnresolved,
      tdm: hasTdmUnresolved,
      champ: hasChampUnresolved,
    });

    const bdmOptions = hasBdmUnresolved
      ? [{ label: 'Unable to resolve assigned BDM name', value: povTeam.cust_bdm }, ...sfTeamOptions]
      : sfTeamOptions;

    const tdmOptions = hasTdmUnresolved
      ? [{ label: 'Unable to resolve assigned TDM name', value: povTeam.cust_tdm }, ...sfTeamOptions]
      : sfTeamOptions;

    const champOptions = hasChampUnresolved
      ? [{ label: 'Unable to resolve assigned Champion name', value: povTeam.cust_champ }, ...sfTeamOptions]
      : sfTeamOptions;

    setPovTeamOptions({ bdm: bdmOptions, tdm: tdmOptions, champion: champOptions });
  }, [sfPovTeam, povTeam, isLoading]);

  // Report validation state to parent
  useEffect(() => {
    const isValid = !!(getValues('bdm') && getValues('tdm') && getValues('champion'));
    onValidationChange(isValid);
  }, [getValues, onValidationChange]);

  // Save team data when form updates
  useEffect(() => {
    const savePovTeam = async () => {
      if (dirtyFields.bdm || dirtyFields.tdm || dirtyFields.champion) {
        isFormDirtyRef.current = false;
        const payload = {
          cust_bdm: getValues('bdm'),
          cust_tdm: getValues('tdm'),
          cust_champ: getValues('champion'),
        };
        try {
          if (!povTeam) {
            await createPovTeam({
              ...payload,
              pov: pov.id,
            });
          } else {
            await updatePovTeam({
              ...povTeam,
              ...payload,
            });
          }
        } catch (error) {
          console.error('Error updating PoV team: ', error);
        }
      }
    };

    if (isFormUpdated && isFormDirtyRef.current) {
      savePovTeam();
      setIsFormUpdated(false);
    }
  }, [isFormUpdated, dirtyFields, povTeam, pov, getValues, createPovTeam, updatePovTeam]);

  return (
    <>
      <div className={style.control}>
        <Controller
          name='bdm'
          control={control}
          rules={{ required: true }}
          render={({ field: { ref, ...field } }) => (
            <HbrSelect
              {...field}
              label='Business Decision Maker'
              placeholder='Select customer team member'
              value={field.value ?? ''}
              disabled={unresolvedFields.bdm}
              onHbr-input={() => (isFormDirtyRef.current = true)}
              onHbr-change={(e) => {
                if (e.target.value === '__add__') {
                  showAddCustomerForm();
                } else {
                  field.onChange(e.target.value);
                  if (isFormDirtyRef.current) {
                    setIsFormUpdated(true);
                  }
                }
              }}
            >
              {povTeamOptions.bdm.map((option) => (
                <HbrOption key={option.value} value={option.value}>
                  {option.label}
                </HbrOption>
              ))}
              <HbrOption key='add-customer' value='__add__'>
                <span style={{ color: '#005073', fontWeight: 'bold' }}>+ Add Customer</span>
              </HbrOption>
            </HbrSelect>
          )}
        />
      </div>
      <div className={style.control}>
        <Controller
          name='tdm'
          control={control}
          rules={{ required: true }}
          render={({ field: { ref, ...field } }) => (
            <HbrSelect
              {...field}
              label='Technical Decision Maker'
              placeholder='Select customer team member'
              value={field.value ?? ''}
              disabled={unresolvedFields.tdm}
              onHbr-input={() => (isFormDirtyRef.current = true)}
              onHbr-change={(e) => {
                if (e.target.value === '__add__') {
                  showAddCustomerForm();
                } else {
                  field.onChange(e.target.value);
                  if (isFormDirtyRef.current) {
                    setIsFormUpdated(true);
                  }
                }
              }}
            >
              {povTeamOptions.tdm.map((option) => (
                <HbrOption key={option.value} value={option.value}>
                  {option.label}
                </HbrOption>
              ))}
              <HbrOption key='add-customer' value='__add__'>
                <span style={{ color: '#005073', fontWeight: 'bold' }}>+ Add Customer</span>
              </HbrOption>
            </HbrSelect>
          )}
        />
      </div>
      <div className={style.control}>
        <Controller
          name='champion'
          control={control}
          rules={{ required: true }}
          render={({ field: { ref, ...field } }) => (
            <HbrSelect
              {...field}
              label='Cisco Champion'
              placeholder='Select customer team member'
              value={field.value ?? ''}
              disabled={unresolvedFields.champ}
              onHbr-input={() => (isFormDirtyRef.current = true)}
              onHbr-change={(e) => {
                if (e.target.value === '__add__') {
                  showAddCustomerForm();
                } else {
                  field.onChange(e.target.value);
                  if (isFormDirtyRef.current) {
                    setIsFormUpdated(true);
                  }
                }
              }}
            >
              {povTeamOptions.champion.map((option) => (
                <HbrOption key={option.value} value={option.value}>
                  {option.label}
                </HbrOption>
              ))}
              <HbrOption key='add-customer' value='__add__'>
                <span style={{ color: '#005073', fontWeight: 'bold' }}>+ Add Customer</span>
              </HbrOption>
            </HbrSelect>
          )}
        />
      </div>
    </>
  );
};

export default CustomerTeamSection;
